import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { config } from '@keytrade/functions';
import { CheckboxGroup } from '@keytrade/components-checkedinput';
import { Icon } from '@keytrade/components-icon';

import BlockWrapper from '@/components/BlockWrapper';
import { mediaQuery } from '@/utils/helpers';
import Container from '@/components/Container';
import NoDataNotification from '@/components/filters/NoDataNotification';
import FilterPill from '@/components/filters/FilterPill';
import useTranslations from '@/hooks/useTranslations';
import translationsKeys from '@/intl/en.json';

import MobileSeminarFilters from './MobileSeminarFilters';
import Seminar, { SeminarType } from './Seminar';

const { list: colors } = config.colors;
const { getFont } = config.fonts;

const { type, topic } = translationsKeys.seminars;
const seminarTypesKeys = Object.keys(type);
const seminarTopicsKeys = Object.keys(topic);

const FilterContainer = styled.div`
  display: none;
  width: 100%;
  max-width: 264px;
  margin-right: 24px;
  ${mediaQuery.md(`
    display: block;
  `)}
`;
const SeminarListContainer = styled.div`
  width: 100%;
  ${mediaQuery.md(`
    width: 100%;
`)}
`;
const SeminarWrapper = styled.div`
  display: flex;
`;
const FilterBlock = styled.div`
  ${mediaQuery.md(`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border-radius: 12px;
    background-color: ${colors.BlueLight};
    padding: 17px 24px;
    margin-bottom: 20px;
  `)}
`;
const FilterPills = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: 80%;
`;
const TotalAmount = styled.span`
  display: none;
  font-size: 14px;
  line-height: 20px;
  text-align: right;
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  color: ${colors.BlueMoon};
  ${mediaQuery.md(`
    display: block;
  `)};
`;
const ClearFilters = styled.span`
  display: none;
  font-size: 14px;
  line-height: 20px;
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  color: ${colors.BlueDawn};
  text-decoration: none;
  cursor: pointer;
  ${mediaQuery.md(`
    display: block;
  `)};
`;

const SeminarListBlock = styled.div`
  ${mediaQuery.md(`
      box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.05),
      0px 2px 7px 0px rgba(0, 0, 0, 0.05);
      border-radius: 12px;
  `)};
`;

const SeminarListTitle = styled.span`
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.6px;
  font-family: ${({ theme }) => getFont(theme, 'bold')};
  text-transform: uppercase;
  color: ${colors.GreyBlue};
  ${mediaQuery.md(`
    margin-bottom: 22px;
  `)};
`;

export type SeminarListingBlockType = {
  __typename: string;
  contentful_id: string;
  name: string;
  node_locale: string;
  title: string;
  allSeminars: {
    nodes: Array<SeminarType>;
  };
};

const SeminarListingBlock: React.FC<SeminarListingBlockType> = ({
  allSeminars,
}) => {
  const { t, to } = useTranslations();

  const languages = [to('en'), to('nl'), to('fr')];
  const types = seminarTypesKeys.map((type) =>
    to(`seminars.type.${type}`, type),
  );
  const topics = seminarTopicsKeys.map((topic) =>
    to(`seminars.topic.${topic}`, topic),
  );

  const [languageFilters, setLanguageFilters] = useState([]);
  const [typeFilters, setTypeFilters] = useState([]);
  const [topicFilters, setTopicFilters] = useState([]);
  const [filteredArray, setFilteredArray] = useState([]);
  const [hasActiveFilters, setActiveFilters] = useState(false);
  const [subscribers, setSubscribers] = useState(false);

  const handleLanguageFilter = (event) => {
    const value = event.target.value;
    if (event.target) {
      setLanguageFilters((prevState) =>
        prevState.includes(value)
          ? prevState.filter((item) => item !== value)
          : [...prevState, value],
      );
    }
  };

  const handleTypeFilter = (event) => {
    const value = event.target.value;
    if (event.target) {
      setTypeFilters((prevState) =>
        prevState.includes(value)
          ? prevState.filter((item) => item !== value)
          : [...prevState, value],
      );
    }
  };

  const handleTopicFilter = (event) => {
    const value = event.target.value;
    if (event.target) {
      setTopicFilters((prevState) =>
        prevState.includes(value)
          ? prevState.filter((item) => item !== value)
          : [...prevState, value],
      );
    }
  };

  useEffect(() => {
    fetch(`${process.env.GATSBY_SEMINARS_API_URL}/v1/seminars/subscribers`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((result) => {
        setSubscribers(result.data);
      });
  }, []);

  useEffect(() => {
    let arr = allSeminars.nodes.filter((seminar) => seminar.private === false);

    // don't allow seminars list to render until we get our subscribers counts
    // update the seminars' full flags when the subscribers counts are fetched
    if (!subscribers) {
      setFilteredArray(arr);
      return;
    } else {
      arr.forEach((seminar) => {
        if (subscribers?.[seminar.jsonId]?.full) {
          seminar.full = true;
        }
      });
    }

    if (topicFilters.length) {
      arr = arr.filter((seminar) =>
        seminar.topics.some((value) => topicFilters.includes(value)),
      );
    }
    if (languageFilters.length) {
      arr = arr.filter((seminar) => languageFilters.includes(seminar.lang));
    }
    if (typeFilters.length) {
      arr = arr.filter((seminar) => typeFilters.includes(seminar.type));
    }
    setActiveFilters(
      topicFilters.length !== 0 ||
        languageFilters.length !== 0 ||
        typeFilters.length !== 0,
    );
    setFilteredArray(arr);
  }, [topicFilters, languageFilters, typeFilters, subscribers]);

  const handleSetFilters = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter((oldValue) => oldValue.filter((item) => item !== value));
  };

  const handleSetLanguageFilter = (value: string) =>
    handleSetFilters(value, setLanguageFilters);
  const handleSetTypeFilter = (value: string) =>
    handleSetFilters(value, setTypeFilters);
  const handleSetTopicFilter = (value: string) =>
    handleSetFilters(value, setTopicFilters);

  const clearFilters = (
    filter?: undefined | 'languages' | 'types' | 'topics',
  ) => {
    switch (filter) {
      case 'languages':
        setLanguageFilters([]);
        break;
      case 'types':
        setTypeFilters([]);
        break;
      case 'topics':
        setTopicFilters([]);
        break;
      default:
        setTopicFilters([]);
        setTypeFilters([]);
        setLanguageFilters([]);
    }

    setActiveFilters(
      topicFilters.length !== 0 ||
        languageFilters.length !== 0 ||
        typeFilters.length !== 0,
    );
  };

  return (
    <BlockWrapper color={'White'} padding={'small'} overflow={'hidden'}>
      <Container>
        <MobileSeminarFilters
          languages={{
            content: languages,
            handleFilter: handleLanguageFilter,
            contentSelected: languageFilters,
          }}
          types={{
            content: types,
            handleFilter: handleTypeFilter,
            contentSelected: typeFilters,
          }}
          topics={{
            content: topics,
            handleFilter: handleTopicFilter,
            contentSelected: topicFilters,
          }}
          clearMobileFilters={clearFilters}
        ></MobileSeminarFilters>
        <SeminarWrapper>
          <FilterContainer>
            <CheckboxGroup
              name='Languages'
              label={t('seminars.languages_label')}
              options={languages}
              value={languageFilters}
              onChange={(e) => handleLanguageFilter(e)}
              errorMessage=''
              margin='0 0 42px 0'
            />
            <CheckboxGroup
              name='Seminar type'
              label={t('seminars.types_label')}
              options={types}
              value={typeFilters}
              onChange={(e) => handleTypeFilter(e)}
              errorMessage=''
              margin='0 0 42px 0'
            />
            <CheckboxGroup
              name='Topics'
              label={t('seminars.topics_label')}
              options={topics}
              value={topicFilters}
              onChange={(e) => handleTopicFilter(e)}
              errorMessage=''
              margin='0 0 42px 0'
            />
          </FilterContainer>
          <SeminarListContainer>
            {hasActiveFilters && (
              <FilterBlock>
                <FilterPills>
                  {languageFilters.map((val) => (
                    <FilterPill
                      key={val}
                      value={val}
                      removeFilter={() => handleSetLanguageFilter(val)}
                    />
                  ))}
                  {typeFilters.map((val) => (
                    <FilterPill
                      key={val}
                      componentType='seminars'
                      value={val}
                      type='type'
                      removeFilter={() => handleSetTypeFilter(val)}
                    />
                  ))}
                  {topicFilters.map((val) => (
                    <FilterPill
                      key={val}
                      componentType='seminars'
                      value={val}
                      type='topic'
                      removeFilter={() => handleSetTopicFilter(val)}
                    />
                  ))}
                  <ClearFilters
                    onClick={(e) => {
                      e.preventDefault();
                      clearFilters();
                    }}
                  >
                    {t('filter.clear_all')}
                  </ClearFilters>
                </FilterPills>
                <TotalAmount>
                  {filteredArray && filteredArray.length === 1
                    ? `${filteredArray.length}${' '}${t(
                        'filter.matches_single',
                      )}`
                    : `${filteredArray.length}${' '}${t('filter.matches')}`}
                </TotalAmount>
              </FilterBlock>
            )}
            {!hasActiveFilters && filteredArray.length === 0 && subscribers && (
              <NoDataNotification
                title={t('seminars.no_scheduling.title')}
                text={t('seminars.no_scheduling.text')}
                icon={<Icon icon='img_calendar' size='7.5rem' />}
              />
            )}
            <div>
              {filteredArray.length > 0 && (
                <SeminarListTitle>
                  {t('seminars.upcomming_session')}
                </SeminarListTitle>
              )}
              {filteredArray.length > 0 && (
                <SeminarListBlock>
                  {React.Children.toArray(
                    filteredArray.map((seminar, i) => (
                      <Seminar key={i} {...seminar} />
                    )),
                  )}
                </SeminarListBlock>
              )}
            </div>
            {hasActiveFilters && filteredArray.length === 0 && subscribers && (
              <NoDataNotification
                title={t('seminars.no_result.title')}
                text={t('seminars.no_result.text')}
                icon={<Icon icon='img_noResults' size='7.5rem' />}
              />
            )}
          </SeminarListContainer>
        </SeminarWrapper>
      </Container>
    </BlockWrapper>
  );
};

export default SeminarListingBlock;
export const SeminarListingBlockFragmentQuery = graphql`
  fragment SeminarListingBlockFragment on ContentfulSeminarListingBlock {
    contentful_id
    __typename
    name
    title
  }
`;
